import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { SearchResult } from '../../types/market';
import { TradierService } from '../../services/TradierService';
import { LoadingSpinner } from './LoadingSpinner';
import { Search } from 'lucide-react';

interface SearchBarProps {
  onSelect: (result: SearchResult) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  onSelect,
  placeholder = 'Search stocks...',
  className = '',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tradierService = useRef(new TradierService());

  const debouncedSearch = useCallback(
    debounce(async (q: string) => {
      if (!q.trim()) { setResults([]); setIsOpen(false); return; }
      setIsLoading(true);
      setError(null);
      try {
        const res = await tradierService.current.searchStocks(q);
        setResults(res);
        setIsOpen(res.length > 0);
        setSelectedIndex(-1);
      } catch {
        setError('Search failed');
        setResults([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => { debouncedSearch(query); }, [query, debouncedSearch]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => (p < results.length - 1 ? p + 1 : 0)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIndex(p => (p > 0 ? p - 1 : results.length - 1)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (selectedIndex >= 0) handleSelect(results[selectedIndex]); }
    if (e.key === 'Escape')    { setIsOpen(false); setSelectedIndex(-1); inputRef.current?.blur(); }
  };

  const handleSelect = (result: SearchResult) => {
    setQuery(result.symbol);
    setIsOpen(false);
    setSelectedIndex(-1);
    onSelect(result);
  };

  return (
    <div ref={searchRef} className={className} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', zIndex: 1 }}>
          <Search size={15} color={focused ? '#3b82f6' : '#475569'} strokeWidth={2} style={{ transition: 'color 0.18s' }} />
        </div>
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="search-results"
          aria-autocomplete="list"
          aria-label="Search for stocks"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { setFocused(true); if (results.length) setIsOpen(true); }}
          onBlur={() => setFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          style={{
            width: '100%',
            padding: '0.75rem 2.75rem',
            background: '#1a2235',
            border: `1px solid ${focused ? '#3b82f6' : '#1e2d45'}`,
            borderRadius: 10,
            color: '#f1f5f9',
            fontSize: '0.9375rem',
            fontFamily: 'inherit',
            outline: 'none',
            transition: 'border-color 0.18s, box-shadow 0.18s',
            boxShadow: focused ? '0 0 0 3px rgba(59,130,246,0.18)' : 'none',
            opacity: disabled ? 0.5 : 1,
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        {isLoading && (
          <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }} aria-live="polite">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          id="search-results"
          role="listbox"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
            background: '#111827',
            border: '1px solid #1e2d45',
            borderRadius: 12,
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
            overflow: 'hidden',
            maxHeight: 280,
            overflowY: 'auto',
            zIndex: 50,
            animation: 'slideUp 0.18s ease-out forwards',
          }}
        >
          {error ? (
            <div role="alert" style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#f87171' }}>{error}</div>
          ) : results.length > 0 ? (
            results.map((result, i) => (
              <div
                key={`${result.symbol}-${result.exchange}`}
                role="option"
                aria-selected={i === selectedIndex}
                onClick={() => handleSelect(result)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.625rem 0.875rem',
                  cursor: 'pointer',
                  background: i === selectedIndex ? '#1a2235' : 'transparent',
                  transition: 'background 0.15s',
                  borderBottom: '1px solid #0f1929',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#1a2235'; }}
                onMouseLeave={e => { e.currentTarget.style.background = i === selectedIndex ? '#1a2235' : 'transparent'; }}
              >
                <div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em' }}>
                    {result.symbol}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 1, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {result.name}
                  </div>
                </div>
                {result.exchange && (
                  <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#334155', background: '#1a2235', padding: '0.1875rem 0.5rem', borderRadius: 6, flexShrink: 0, marginLeft: 8 }}>
                    {result.exchange}
                  </span>
                )}
              </div>
            ))
          ) : query.trim() && !isLoading ? (
            <div role="status" style={{ padding: '0.875rem 1rem', fontSize: '0.8125rem', color: '#475569', textAlign: 'center' }}>
              No results for "{query}"
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

function debounce<T extends (...args: any[]) => any>(fn: T, wait: number) {
  let t: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}
